/**
 * MenuSystem.ts
 * FF1-style menu rendering and navigation
 */

import { Renderer } from './Renderer';
import { MenuItem } from './types/engine';

export class Menu {
  items: MenuItem[];
  selectedIndex: number;
  visible: boolean;
  x: number;
  y: number;
  width: number;
  itemHeight: number;
  maxVisibleItems: number;
  scrollOffset: number;
  title?: string;

  constructor(
    items: MenuItem[],
    x: number,
    y: number,
    width: number,
    maxVisibleItems: number = 8
  ) {
    this.items = items;
    this.selectedIndex = 0;
    this.visible = true;
    this.x = x;
    this.y = y;
    this.width = width;
    this.itemHeight = 12;
    this.maxVisibleItems = maxVisibleItems;
    this.scrollOffset = 0;
  }

  /**
   * Render the menu
   */
  render(renderer: Renderer): void {
    if (!this.visible) return;

    // Calculate menu height
    const visibleCount = Math.min(this.items.length, this.maxVisibleItems);
    const menuHeight = visibleCount * this.itemHeight + 16; // +16 for padding

    // Draw title if exists
    let contentY = this.y + 8;
    if (this.title) {
      renderer.drawText(this.title, this.x + 8, contentY, {
        font: 'ff1',
        fontSize: 8,
        color: '#e94560',
      });
      contentY += this.itemHeight;
    }

    // Draw items
    const startIndex = this.scrollOffset;
    const endIndex = Math.min(startIndex + this.maxVisibleItems, this.items.length);

    for (let i = startIndex; i < endIndex; i++) {
      const item = this.items[i];
      const itemY = contentY + (i - startIndex) * this.itemHeight;
      const isSelected = i === this.selectedIndex;

      // Draw cursor
      if (isSelected) {
        renderer.drawCursor(this.x + 4, itemY + 2);
      }

      // Draw item text - selected items use accent color
      const textColor = item?.disabled ? '#555555' : (isSelected ? '#ffd700' : '#ffffff');
      renderer.drawText(item?.label ?? '', this.x + 12, itemY, textColor);

      // Draw submenu indicator
      if (item?.submenu && item.submenu.length > 0) {
        renderer.drawText('>', this.x + this.width - 12, itemY, textColor);
      }
    }

    // Draw scroll indicators
    if (this.scrollOffset > 0) {
      // Up arrow
      renderer.drawText('^', this.x + this.width / 2, this.y + 4, '#888888');
    }
    if (endIndex < this.items.length) {
      // Down arrow
      renderer.drawText('v', this.x + this.width / 2, this.y + menuHeight - 10, '#888888');
    }
  }

  /**
   * Move cursor up
   */
  moveUp(): void {
    let newIndex = this.selectedIndex - 1;

    // Find previous non-disabled item
    while (newIndex >= 0 && this.items[newIndex]?.disabled) {
      newIndex--;
    }

    if (newIndex >= 0) {
      this.selectedIndex = newIndex;
      this.adjustScroll();
    }
  }

  /**
   * Move cursor down
   */
  moveDown(): void {
    let newIndex = this.selectedIndex + 1;

    // Find next non-disabled item
    while (newIndex < this.items.length && this.items[newIndex]?.disabled) {
      newIndex++;
    }

    if (newIndex < this.items.length) {
      this.selectedIndex = newIndex;
      this.adjustScroll();
    }
  }

  /**
   * Adjust scroll offset to keep selected item visible
   */
  private adjustScroll(): void {
    if (this.selectedIndex < this.scrollOffset) {
      this.scrollOffset = this.selectedIndex;
    } else if (this.selectedIndex >= this.scrollOffset + this.maxVisibleItems) {
      this.scrollOffset = this.selectedIndex - this.maxVisibleItems + 1;
    }
  }

  /**
   * Select current item
   */
  select(): MenuItem | null {
    const item = this.items[this.selectedIndex];
    if (item && !item.disabled) {
      return item;
    }
    return null;
  }

  /**
   * Get selected item
   */
  getSelected(): MenuItem | null {
    return this.items[this.selectedIndex] || null;
  }

  /**
   * Get selected item (alias for compatibility)
   */
  getSelectedItem(): MenuItem | null {
    return this.getSelected();
  }

  /**
   * Set items
   */
  setItems(items: MenuItem[]): void {
    this.items = items;
    this.selectedIndex = 0;
    this.scrollOffset = 0;
  }

  /**
   * Show menu
   */
  show(): void {
    this.visible = true;
  }

  /**
   * Hide menu
   */
  hide(): void {
    this.visible = false;
  }

  /**
   * Reset selection
   */
  reset(): void {
    this.selectedIndex = 0;
    this.scrollOffset = 0;
  }
}

/**
 * Two-column menu (e.g., for items with quantities)
 */
export class TwoColumnMenu extends Menu {
  rightColumnX: number;

  constructor(
    items: MenuItem[],
    x: number,
    y: number,
    width: number,
    maxVisibleItems: number = 8
  ) {
    super(items, x, y, width, maxVisibleItems);
    this.rightColumnX = width - 32;
  }

  render(renderer: Renderer): void {
    if (!this.visible) return;

    // Calculate menu height
    const visibleCount = Math.min(this.items.length, this.maxVisibleItems);
    const menuHeight = visibleCount * this.itemHeight + 16;

    // Draw card background
    renderer.drawCard(this.x, this.y, this.width, menuHeight, false);

    // Draw items
    const startIndex = this.scrollOffset;
    const endIndex = Math.min(startIndex + this.maxVisibleItems, this.items.length);

    for (let i = startIndex; i < endIndex; i++) {
      const item = this.items[i];
      const itemY = this.y + 8 + (i - startIndex) * this.itemHeight;
      const isSelected = i === this.selectedIndex;

      // Draw cursor
      if (isSelected) {
        renderer.drawCursor(this.x + 4, itemY + 2);
      }

      // Draw item name (left)
      const textColor = item?.disabled ? '#555555' : (isSelected ? '#ffd700' : '#ffffff');
      renderer.drawText(item?.label ?? '', this.x + 12, itemY, textColor);

      // Draw value (right) - stored in value field
      if (item?.value) {
        renderer.drawText(item.value, this.x + this.rightColumnX, itemY, '#ffd700');
      }
    }
  }
}

/**
 * Battle Menu
 * Specialized menu for combat actions
 */
export class BattleMenu {
  mainMenu: Menu;
  magicMenu: Menu | null;
  itemMenu: Menu | null;
  targetMenu: Menu | null;
  activeMenu: 'main' | 'magic' | 'item' | 'target';

  constructor(x: number, y: number) {
    this.mainMenu = new Menu(
      [
        { label: 'FIGHT', value: 'fight' },
        { label: 'MAGIC', value: 'magic' },
        { label: 'ITEM', value: 'item' },
        { label: 'RUN', value: 'run' }
      ],
      x,
      y,
      80,
      4
    );

    this.magicMenu = null;
    this.itemMenu = null;
    this.targetMenu = null;
    this.activeMenu = 'main';
  }

  render(renderer: Renderer): void {
    switch (this.activeMenu) {
      case 'main':
        this.mainMenu.render(renderer);
        break;
      case 'magic':
        this.magicMenu?.render(renderer);
        break;
      case 'item':
        this.itemMenu?.render(renderer);
        break;
      case 'target':
        this.targetMenu?.render(renderer);
        break;
    }
  }

  handleInput(key: string): string | null {
    const activeMenu = this.getActiveMenu();
    if (!activeMenu) return null;

    switch (key) {
      case 'up':
        activeMenu.moveUp();
        break;
      case 'down':
        activeMenu.moveDown();
        break;
      case 'a': // Confirm
        const selected = activeMenu.select();
        if (selected) {
          return selected.value;
        }
        break;
      case 'b': // Back
        if (this.activeMenu !== 'main') {
          this.activeMenu = 'main';
          return 'back';
        }
        break;
    }

    return null;
  }

  private getActiveMenu(): Menu | null {
    switch (this.activeMenu) {
      case 'main':
        return this.mainMenu;
      case 'magic':
        return this.magicMenu;
      case 'item':
        return this.itemMenu;
      case 'target':
        return this.targetMenu;
      default:
        return null;
    }
  }

  setMagicList(spells: MenuItem[]): void {
    this.magicMenu = new Menu(spells, this.mainMenu.x + 90, this.mainMenu.y, 100, 8);
  }

  setItemList(items: MenuItem[]): void {
    this.itemMenu = new TwoColumnMenu(items, this.mainMenu.x + 90, this.mainMenu.y, 100, 8);
  }

  setTargetList(targets: MenuItem[]): void {
    this.targetMenu = new Menu(targets, this.mainMenu.x + 90, this.mainMenu.y, 80, 8);
  }

  switchToMenu(menu: 'main' | 'magic' | 'item' | 'target'): void {
    this.activeMenu = menu;
  }

  reset(): void {
    this.activeMenu = 'main';
    this.mainMenu.reset();
    this.magicMenu?.reset();
    this.itemMenu?.reset();
    this.targetMenu?.reset();
  }
}


// MenuSystem is an alias for Menu (backwards compatibility)
export { Menu as MenuSystem };
